import styled from 'styled-components';
import { Colors } from '../../utils/figmaColors';
import { HORIZONTAL_PADDING } from '../../components/layout/constants';

export const OuterContainer = styled.div`
  background-color: ${Colors.darkBackground};
  width: 100%;
  max-width: 100vw;
  padding: clamp(24px, 3vw, 48px) 0 clamp(40px, 5vw, 80px);
  margin: 0;
  overflow-x: hidden;
  overflow-y: visible;
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: clamp(600px, 100vh, 964px);
  box-sizing: border-box;
  
  @media (min-width: 769px) and (max-width: 1199px) {
    padding-top: clamp(4px, 2vw, 20px);
  }
  
  /* Hero section only, mobile: remove bottom gap (empty space below phones) */
  @media (max-width: 768px) {
    padding-top: clamp(24px, 5vw, 40px);
    padding-bottom: 0;
    margin-bottom: -120px;
    min-height: auto;
  }
`;

export const HeroContentWrapper = styled.div`
  max-width: 1440px;
  width: 100%;
  margin: 0 auto;
  padding-left: ${HORIZONTAL_PADDING};
  padding-right: ${HORIZONTAL_PADDING};
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: clamp(24px, 5vw, 80px);
  position: relative;
  min-width: 0;
  box-sizing: border-box;
  overflow: visible;
  
  @media (min-width: 1200px) {
    gap: 32px;
    align-items: flex-start;
  }
  
  @media (max-width: 1199px) {
    flex-direction: column;
    align-items: center;
    gap: clamp(32px, 5vw, 56px);
  }
`;

export const LeftContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(32px, 5vw, 60px);
  flex: 1 1 0;
  min-width: 0;
  max-width: min(678px, 100%);
  padding-top: clamp(0px, 2vw, 20.72px);
  box-sizing: border-box;
  
  @media (min-width: 1200px) {
    flex: 0 0 678px;
    width: 678px;
    min-width: 678px;
    max-width: 678px;
    padding-top: clamp(24px, 2.5vw, 40px);
  }
  
  @media (max-width: 1199px) {
    flex: 0 0 auto;
    max-width: 100%;
    align-items: center;
    text-align: center;
    padding-top: 0;
  }
`;

export const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(24px, 3vw, 32px);
  width: 100%;
`;

export const Title = styled.h1`
  font-family: 'Mulish', sans-serif;
  font-size: clamp(38px, 5.5vw + 1.25rem, 72px);
  font-weight: 700;
  line-height: 1.2;
  color: ${Colors.white};
  margin: 0;
  letter-spacing: -0.025em;
  width: 100%;
  
  @media (min-width: 1200px) {
    font-size: clamp(40px, 4.2vw + 0.5rem, 56px);
  }
  
  @media (min-width: 769px) and (max-width: 1199px) {
    font-size: clamp(32px, 5vw + 0.5rem, 44px);
    font-weight: 700;
  }
  
  @media (max-width: 768px) {
    font-size: clamp(28px, 7.2vw, 36px);
    font-weight: 800;
    letter-spacing: -0.02em;
    max-width: 100%;
  }
`;

export const Subtitle = styled.p`
  font-family: 'Mulish', sans-serif;
  font-size: clamp(18px, 2.5vw, 24px);
  font-weight: 400;
  line-height: 1.5;
  color: ${Colors.lightGray};
  margin: 0;
  max-width: min(718px, 100%);
  
  @media (max-width: 1199px) {
    text-align: center;
    margin-inline: auto;
  }
`;

export const StoreButtons = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(8px, 1.5vw, 12.45px);
  flex-wrap: wrap;
  
  @media (max-width: 1199px) {
    justify-content: center;
  }
`;

export const StoreButton = styled.a`
  display: inline-block;
  transition: transform 0.2s ease, opacity 0.2s ease;
  
  &:hover {
    transform: scale(1.05);
    opacity: 0.9;
  }
  
  img {
    height: clamp(44px, 5vw, 58.87px);
    width: auto;
    max-width: 100%;
  }
`;

export const CompanyLogos = styled.div`
  width: 100%;
  max-width: min(704px, 100%);
  overflow: hidden;
  position: relative;
`;

export const CompanyLogosWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(16px, 2.5vw, 24px);
  width: fit-content;
  animation: scrollLogos 30s linear infinite;
  
  @keyframes scrollLogos {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(-50%);
    }
  }
`;

export const CompanyLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-radius: clamp(12px, 1.5vw, 16px);
  background: transparent;
  height: clamp(50px, 6vw, 77.5px);
  min-width: clamp(90px, 12vw, 120px);
  flex-shrink: 0;
  max-width: clamp(120px, 15vw, 172.44px);
  
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    filter: brightness(0) invert(1);
  }
`;

export const VisualContainer = styled.div`
  position: relative;
  flex: 0 1 auto;
  min-width: 200px;
  max-width: min(450px, 42%);
  width: clamp(260px, 30vw, 450px);
  height: clamp(430px, 48vw, 750px);
  display: flex;
  align-items: stretch;
  justify-content: center;
  box-sizing: border-box;
  overflow: visible;
  
  @media (min-width: 1200px) {
    flex: 1 1 0;
    min-width: 200px;
    width: 100%;
    max-width: none;
    height: clamp(620px, 56vw, 820px);
    padding-top: clamp(80px, 8vw, 120px);
    margin-top: -115px;
    margin-inline: auto;
    overflow: visible;
  }
  
  @media (max-width: 1199px) {
    flex: 0 0 auto;
    width: clamp(340px, 58vw, 460px);
    max-width: 100%;
    min-width: 280px;
    height: clamp(560px, 88vw, 700px);
    margin-top: 0;
    margin-inline: auto;
  }
  
  @media (max-width: 768px) {
    width: min(96vw, 420px);
    min-width: 300px;
    height: clamp(580px, 148vw, 700px);
    margin-inline: auto;
  }
`;

export const HeroImgInner = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  padding: 5%;
  box-sizing: border-box;
  overflow: visible;
  /* Buffer for rotated phones; no cropping */
  
  @media (min-width: 1200px) {
    padding: 15% 5% 5% 5%;
  }
  
  @media (max-width: 768px) {
    padding: 6% 4% 4% 4%;
  }
`;

export const HeroImgContainer = styled.div`
  position: absolute;
  top: -20%;
  left: 0;
  right: 0;
  bottom: -5%;
  width: 100%;
  height: 125%;
  box-sizing: border-box;
  overflow: visible;
  
  @media (min-width: 1200px) {
    top: -18%;
    height: 118%;
  }
  
  @media (max-width: 1199px) {
    left: 50%;
    right: auto;
    width: 94%;
    transform: translateX(-50%);
    top: -12%;
    height: 112%;
  }
  
  @media (max-width: 768px) {
    left: 50%;
    transform: translateX(-50%);
    top: -8%;
    height: 108%;
  }
`;

interface HeroImageProps {
  $isBack?: boolean;
}

export const HeroImage = styled.img<HeroImageProps>`
  position: absolute;
  object-fit: contain;
  transform: rotate(${props => props.$isBack ? '-2deg' : '2deg'});
  z-index: ${props => props.$isBack ? 1 : 2};
  max-width: 100%;
  max-height: 100%;
  
  /* Desktop: larger phones, centered via % positioning */
  width: ${props => props.$isBack 
    ? 'clamp(200px, 26vw, 300px)' 
    : 'clamp(240px, 30vw, 360px)'};
  height: ${props => props.$isBack 
    ? 'clamp(400px, 52vw, 600px)' 
    : 'clamp(440px, 58vw, 680px)'};
  left: ${props => props.$isBack ? '2%' : '20%'};
  top: ${props => props.$isBack 
    ? 'clamp(6px, 1.5vw, 14px)' 
    : 'clamp(32px, 6vw, 72px)'};
  
  @media (min-width: 1200px) {
    top: ${props => props.$isBack ? 'clamp(60px, 7vw, 90px)' : 'clamp(90px, 10vw, 130px)'};
  }
  
  @media (max-width: 1199px) {
    width: ${props => props.$isBack 
      ? 'min( clamp(180px, 24vw, 280px), 56% )' 
      : 'min( clamp(210px, 28vw, 340px), 68% )'};
    height: ${props => props.$isBack 
      ? 'min( clamp(360px, 48vw, 540px), 86% )' 
      : 'min( clamp(400px, 54vw, 620px), 96% )'};
    left: ${props => props.$isBack ? '6%' : '22%'};
    top: ${props => props.$isBack 
      ? 'clamp(20px, 3vw, 40px)' 
      : 'clamp(40px, 6vw, 70px)'};
  }
  
  @media (max-width: 768px) {
    width: ${props => props.$isBack 
      ? 'min( clamp(200px, 34vw, 300px), 64% )' 
      : 'min( clamp(235px, 38vw, 360px), 76% )'};
    height: ${props => props.$isBack 
      ? 'min( clamp(380px, 64vw, 560px), 94% )' 
      : 'min( clamp(420px, 70vw, 620px), 100% )'};
    left: ${props => props.$isBack ? '8%' : '24%'};
    top: ${props => props.$isBack 
      ? 'clamp(15px, 3vw, 30px)' 
      : 'clamp(30px, 5vw, 50px)'};
  }
  
  ${props => !props.$isBack ? `
    filter: drop-shadow(0 2.95px 10.34px rgba(0, 0, 0, 0.4));
  ` : ''}
`;
